import { UserProfileFilters, UserProfileResult } from '@mews-commander/data-access';
import {
    Button,
    IconName,
    Table,
    TableActiveColumnFilters,
    TableHeader,
    TableProvider,
    useTableState,
} from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getActiveFilterCount, useUserProfilesData } from '../../../queries';
import { useUserContext } from '../../../context';
import { useProvisionedUserIds } from '../../../hooks/useIsUserProvisioned';
import { UserType } from '../../../types';
import { AddMultipleUsersButton } from '../../AddGlobalUsers';
import { AddUserButton } from '../../AddLocalUser';
import { useBulkDelete } from '../../DeleteUser';
import { UserDetail } from '../../UserDetail';
import { WithFilterValues } from '../filterSchema';
import { useActiveUserDetail } from '../utils';
import { useColumns } from './tableMapping';

export const UsersTable = ({ filterValues, toggleSidebar }: WithFilterValues & { toggleSidebar: () => void }) => {
    const { userType } = useUserContext();

    const { BulkActions, unselectRow, rowSelection } = useBulkDelete();

    const columns = useColumns({ onRowDelete: unselectRow });

    const [tableState, setTableState] = useTableState<UserProfileResult, UserProfileFilters>({
        pagination: { pageSize: 50 },
    });

    const { data: tableData, isFetching } = useUserProfilesData({
        tableState,
        filterValues,
    });

    const totalRowCount = tableData?.totalRowCount || 0;
    const userProfiles = tableData?.data || [];

    const provisionedUserIds = useProvisionedUserIds(userProfiles);
    const activeUserDetail = useActiveUserDetail(userProfiles);

    return (
        <>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <TableHeader
                    title={<LocalizedText textKey={TextKey.UserPlural} />}
                    subTitle={totalRowCount}
                    globalSearch={{ placeholder: <LocalizedText textKey={TextKey.UsersSearchHint} /> }}
                    rightSide={
                        <>
                            <Button
                                icon={IconName.Filter}
                                type="button"
                                onClick={toggleSidebar}
                                counter={getActiveFilterCount(filterValues) || undefined}
                            >
                                <LocalizedText textKey={TextKey.Filter} />
                            </Button>
                            {userType === UserType.Portfolio ? <AddUserButton /> : <AddMultipleUsersButton />}
                        </>
                    }
                />

                <TableActiveColumnFilters />

                {userType === UserType.Portfolio ? null : BulkActions}

                <Table
                    uniqueRowId="userId"
                    data={userProfiles}
                    amountOfRows={totalRowCount}
                    loading={isFetching}
                    fixedFirstColumn
                    fixedLastColumn
                    rowSelection={
                        userType === UserType.Portfolio
                            ? undefined
                            : {
                                  ...rowSelection,
                                  enableRowSelection: (row) => !provisionedUserIds?.includes(row.original.userId),
                              }
                    }
                />
            </TableProvider>
            {activeUserDetail ? <UserDetail user={activeUserDetail} /> : null}
        </>
    );
};
